class RasaSlideshow extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.timer = null;
    this.isHovered = false;
    this.hasFocusWithin = false;
    this.isPageVisible = document.visibilityState === 'visible';
    this.isInViewport = true;
    this.editorPaused = false;
    this.swipeStart = null;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.slides = Array.from(this.querySelectorAll('[data-rasa-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-rasa-dot-index]'));
    this.status = this.querySelector('[data-rasa-status]');
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.autoplayDelay = Math.max(3000, Number(this.dataset.autoplayDelay) || 5000);
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverQuery = window.matchMedia('(hover: hover)');

    const initialIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.currentIndex = initialIndex >= 0 ? initialIndex : 0;

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerCancel);
    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.isInViewport = Boolean(entry?.isIntersecting);
          this.syncPlayback();
          this.restartAutoplay();
        },
        { threshold: 0.1 }
      );
      this.intersectionObserver.observe(this);
    }

    this.syncSlides(false);
    this.syncPlayback();
    this.restartAutoplay();
  }

  disconnectedCallback() {
    this.clearAutoplay();
    this.intersectionObserver?.disconnect();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('pointerup', this.onPointerUp);
    this.removeEventListener('pointercancel', this.onPointerCancel);
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.reducedMotionQuery?.removeEventListener('change', this.onReducedMotionChange);
    this.initialized = false;
  }

  get isReducedMotion() {
    return Boolean(this.reducedMotionQuery?.matches);
  }

  get canAutoplay() {
    return (
      this.autoplayEnabled &&
      this.isConnected &&
      this.slides.length > 1 &&
      !this.isReducedMotion &&
      this.isPageVisible &&
      this.isInViewport &&
      !this.editorPaused &&
      !(this.pauseOnHover && this.hoverQuery.matches && this.isHovered) &&
      !this.hasFocusWithin
    );
  }

  get canPlayVideo() {
    return this.isConnected && this.isPageVisible && this.isInViewport && !this.isReducedMotion;
  }

  onClick(event) {
    const control = event.target.closest('[data-rasa-action], [data-rasa-dot-index]');
    if (!control || !this.contains(control)) return;

    event.preventDefault();

    if (control.hasAttribute('data-rasa-dot-index')) {
      this.goTo(Number(control.dataset.rasaDotIndex), true);
      return;
    }

    if (control.dataset.rasaAction === 'previous') {
      this.goTo(this.currentIndex - 1, true);
    } else if (control.dataset.rasaAction === 'next') {
      this.goTo(this.currentIndex + 1, true);
    }
  }

  onKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.currentIndex - 1, true);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.currentIndex + 1, true);
    }
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' || event.button !== 0) return;

    this.swipeStart = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  }

  onPointerUp(event) {
    if (!this.swipeStart || this.swipeStart.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - this.swipeStart.x;
    const deltaY = event.clientY - this.swipeStart.y;
    this.swipeStart = null;

    if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

    event.preventDefault();
    this.goTo(deltaX < 0 ? this.currentIndex + 1 : this.currentIndex - 1, true);
  }

  onPointerCancel() {
    this.swipeStart = null;
  }

  onMouseEnter() {
    this.isHovered = true;
    this.restartAutoplay();
  }

  onMouseLeave() {
    this.isHovered = false;
    this.restartAutoplay();
  }

  onFocusIn() {
    this.hasFocusWithin = true;
    this.restartAutoplay();
  }

  onFocusOut() {
    window.setTimeout(() => {
      this.hasFocusWithin = this.contains(document.activeElement);
      this.restartAutoplay();
    }, 0);
  }

  onVisibilityChange() {
    this.isPageVisible = document.visibilityState === 'visible';
    this.syncPlayback();
    this.restartAutoplay();
  }

  onReducedMotionChange() {
    this.syncPlayback();
    this.restartAutoplay();
  }

  onBlockSelect(event) {
    const slide = event.target instanceof Element ? event.target.closest('[data-rasa-slide]') : null;
    if (!slide || !this.contains(slide)) return;

    const index = this.slides.indexOf(slide);
    if (index < 0) return;

    this.editorPaused = true;
    this.goTo(index, false);
  }

  onBlockDeselect(event) {
    const slide = event.target instanceof Element ? event.target.closest('[data-rasa-slide]') : null;
    if (!slide || !this.contains(slide)) return;

    this.editorPaused = false;
    this.restartAutoplay();
  }

  goTo(index, userInitiated = false) {
    if (this.slides.length < 2) {
      this.currentIndex = 0;
      this.syncSlides(false);
      this.syncPlayback();
      this.clearAutoplay();
      return;
    }

    const normalizedIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
    const changed = normalizedIndex !== this.currentIndex;
    this.currentIndex = normalizedIndex;

    if (changed) {
      this.syncSlides(true);
      this.syncPlayback();
    }

    if (userInitiated || changed) {
      this.restartAutoplay();
    }
  }

  syncSlides(announce) {
    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.inert = !isActive;
      this.syncFocusableElements(slide, isActive);
    });

    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (this.status) {
      this.status.textContent = `Slayt ${this.currentIndex + 1} / ${this.slides.length}`;
      if (!announce) this.status.setAttribute('aria-live', 'off');
      window.requestAnimationFrame(() => this.status?.setAttribute('aria-live', 'polite'));
    }
  }

  syncFocusableElements(slide, isActive) {
    const focusableElements = slide.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]'
    );

    focusableElements.forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      if (isActive) {
        if (element.dataset.rasaPreviousTabindex === '') {
          element.removeAttribute('tabindex');
        } else if (element.dataset.rasaPreviousTabindex !== undefined) {
          element.setAttribute('tabindex', element.dataset.rasaPreviousTabindex);
        }
        delete element.dataset.rasaPreviousTabindex;
      } else {
        if (element.dataset.rasaPreviousTabindex === undefined) {
          element.dataset.rasaPreviousTabindex = element.getAttribute('tabindex') ?? '';
        }
        element.setAttribute('tabindex', '-1');
      }
    });
  }

  syncPlayback() {
    this.slides.forEach((slide, index) => {
      const video = slide.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) return;

      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';

      if (index === this.currentIndex && this.canPlayVideo) {
        const playPromise = video.play();
        if (playPromise instanceof Promise) playPromise.catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  clearAutoplay() {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  restartAutoplay() {
    this.clearAutoplay();
    if (!this.canAutoplay) return;

    this.timer = window.setTimeout(() => {
      this.goTo(this.currentIndex + 1, false);
    }, this.autoplayDelay);
  }
}

if (!customElements.get('rasa-slideshow')) {
  customElements.define('rasa-slideshow', RasaSlideshow);
}
